import React from "react";
import { useNavigate } from "react-router-dom";
import { Star, BadgeCheck } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { translations } from "../../i18n/translations";
import { expertSlug } from "../../mock/mock";
import { mediaUrl } from "../../lib/media";

const TierBadge = ({ tier }) => (
  <span
    className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${
      tier === "gold" ? "bg-amber-400" : "bg-sky-500"
    }`}
  >
    <BadgeCheck className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
  </span>
);

const ExpertCard = ({ expert, onClick }) => {
  const { lang, loc, t } = useLang();
  const navigate = useNavigate();
  const catLabel = translations[lang]?.categories?.[expert.category] || expert.category;

  const handleClick = () => {
    if (typeof onClick === "function") onClick(expert);
    else navigate(`/expert/${expertSlug(expert)}`);
  };

  return (
    <button
      onClick={handleClick}
      className="group text-start bg-white rounded-3xl overflow-hidden border border-neutral-100 card-hover w-full"
    >
      <div className="relative aspect-[4/5] overflow-hidden">
        <img
          src={mediaUrl(expert.photo)}
          alt={expert.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

        <div className="absolute top-3 start-3">
          <span className="px-2.5 py-1 rounded-full bg-white/90 backdrop-blur text-[11px] font-semibold text-neutral-800 shadow-sm">
            {catLabel}
          </span>
        </div>
        <div className="absolute top-3 end-3 flex items-center gap-1 px-2 py-1 rounded-full bg-white/90 backdrop-blur shadow-sm">
          <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
          <span className="text-[11px] font-bold text-neutral-800">{expert.rating.toFixed(1)}</span>
        </div>

        <div className="absolute bottom-0 inset-x-0 p-4 text-white">
          <div className="flex items-center gap-1.5">
            <h3 className="font-display font-bold text-lg leading-tight">{expert.name}</h3>
            <TierBadge tier={expert.tier} />
          </div>
        </div>
      </div>

      <div className="p-4">
        <p className="text-sm text-neutral-500 leading-snug line-clamp-2 min-h-[40px]">{loc(expert.title)}</p>
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-baseline gap-1">
            <span className="font-display font-extrabold text-lg text-neutral-900">${expert.price}</span>
            <span className="text-xs text-neutral-400">{t("expert.perSession")}</span>
          </div>
          <span className="text-sm font-semibold text-indigo-600 group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5 transition-transform">
            {t("expert.book")} →
          </span>
        </div>
      </div>
    </button>
  );
};

export default ExpertCard;
export { TierBadge };
